import { useQuery } from '@tanstack/react-query';
import { operationalAccessService } from '@/services/operational.service';
import { atLeast, type AccessLevel, type OperationalModule } from '@/types/operational';

/**
 * The caller's own operational access.
 *
 * Used to decide what to *show*. It is never what decides what somebody may
 * do — every endpoint re-checks on the server, and a menu item that is hidden
 * here still leads to a 403 if it is reached another way. The point is not to
 * offer a person a page that will refuse them.
 *
 * A failure resolves to "no access" rather than throwing: a signed-in user
 * whose access query failed should see the financial app without Operational
 * Control, not an error screen.
 */
export function useOperationalAccess() {
  const { data, isPending } = useQuery({
    queryKey: ['operational', 'access', 'me'],
    queryFn: ({ signal }) => operationalAccessService.me(signal),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const modules = (data?.modules ?? {}) as Record<OperationalModule, AccessLevel>;

  return {
    loading: isPending,
    isOwner: data?.is_owner ?? false,
    hasAny: data?.has_any ?? false,
    level: (module: OperationalModule): AccessLevel => modules[module] ?? 'NONE',
    can: (module: OperationalModule, required: AccessLevel = 'VIEW') =>
      atLeast(modules[module], required),
  };
}
